import React from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { track } from '@amplitude/analytics-browser'
import { Icon } from '@iconify/react'
import { Button, colors } from 'src/components/app-ui'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import {
  CardPublishState,
  useCoursePublish,
} from 'src/contexts/course-publish-context'

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    border: '1px solid #FCE3B4',
    borderRadius: '10px',
    background: '#FEF3DD',
    color: colors.warningDark,
    fontSize: '14px',
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  strong: {
    fontWeight: 600,
  },
})

const pagesLabel = count => `${count} ${count === 1 ? 'page' : 'pages'}`

/**
 * Shown on every course tab while a published course has pages edited since
 * they went live: learners still see the last published version. "Publish
 * changes" asks first, then publishes those pages.
 */
const UnpublishedBanner = ({ course }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const publishState = useCoursePublish()

  if (!publishState?.isLoaded || !publishState.isPublished) return null
  const edited = publishState.pages.filter(
    page => page.state === CardPublishState.PUBLISHED_WITH_CHANGES,
  )
  if (edited.length === 0) return null

  const publish = async () => {
    try {
      await publishState.publishChanges(edited.map(page => page.cardId))
      track('course_changes_published')
      enqueueSnackbar('Changes published', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Could not publish these changes.', { variant: 'error' })
    } finally {
      dispatch(messageBoxHideAction())
    }
  }

  const confirm = () =>
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => dispatch(messageBoxHideAction()),
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Publish',
              ButtonActionType.YES,
              publish,
            ),
          ],
          title: `Publish changes to ${course?.name || 'this course'}?`,
          subTitle: null,
          description: `${edited.length} edited ${
            edited.length === 1 ? 'page' : 'pages'
          } will go live for learners.`,
          maxWidth: '500px',
          variant: MessageBoxVariant.NOTICE,
        }),
      ),
    )

  return (
    <div className={classes.banner}>
      <Icon icon="tabler:alert-circle" width={18} height={18} />
      <span className={classes.text}>
        <span className={classes.strong}>
          {pagesLabel(edited.length)}{' '}
          {edited.length === 1 ? 'has' : 'have'} unpublished changes.
        </span>{' '}
        Learners still see the last published version.
      </span>
      <Button size="small" onClick={confirm}>
        Publish changes
      </Button>
    </div>
  )
}

export default UnpublishedBanner
